import React, { useState, useRef } from 'react';
import {
  Container, Card, Typography, TextField, FormControlLabel, Checkbox,
  Button, Box, Stack, Alert, AlertColor, AlertTitle,
  Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import SignaturePad, { SignaturePadRef } from '../Components/SignaturePad.tsx';
import PublicApiService from '../Services/publicApiService.ts';

const ConsentForm = () => {
  const initialFormState = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    address: '',
    physician: '',
    allergies: '',
    bleedingDisorders: '',
    medicalConditions: '',
    medications: '',
    emergencyContact: '',
    howDidYouHear: '',
    typeOfPiercing: '',
    typeOfJewelryUsed: '',
    nameOfPiercer: '',
    acknowledgments: {} as { [key: number]: { checked: boolean; label: string } },
    date: '',
    piercerDate: '',
    photoIdVerified: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [phoneError, setPhoneError] = useState('');
  const clientSigRef = useRef<SignaturePadRef>(null);
  const piercerSigRef = useRef<SignaturePadRef>(null);
  const [alert, setAlert] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'warning',
  });

  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Photo ID upload state
  const [photoIdFile, setPhotoIdFile] = useState<string | null>(null);
  const [photoIdFileName, setPhotoIdFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAlert({ open: true, message: 'Please upload an image file (JPG, PNG, etc.).', severity: 'error' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setAlert({ open: true, message: 'Photo must be less than 5MB.', severity: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoIdFile(reader.result as string);
      setPhotoIdFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const removePhotoId = () => {
    setPhotoIdFile(null);
    setPhotoIdFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let phoneNumber = e.target.value.replace(/\D/g, '');

    if (phoneNumber.startsWith('1')) {
      phoneNumber = phoneNumber.slice(1);
    }

    if (phoneNumber.length > 10) {
      phoneNumber = phoneNumber.slice(0, 10);
    }

    if (phoneNumber.length !== 10 && phoneNumber.length > 0) {
      setPhoneError('Phone number must be exactly 10 digits.');
    } else {
      setPhoneError('');
    }

    setFormData({ ...formData, phone: phoneNumber });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name !== 'phone') {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAcknowledgment = (index: number, label: string) => {
    setFormData({
      ...formData,
      acknowledgments: {
        ...formData.acknowledgments,
        [index]: { checked: !formData.acknowledgments[index]?.checked, label }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (phoneError) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    // Validate phone is 10 digits
    if (formData.phone.length !== 10) {
      setAlert({ open: true, message: 'Please enter a valid 10-digit phone number.', severity: 'error' });
      return;
    }

    // Validate all acknowledgments are checked
    const allChecked = acknowledgmentStatements.every(
      (_, index) => formData.acknowledgments[index]?.checked
    );
    if (!allChecked) {
      setAlert({ open: true, message: 'Please acknowledge all consent statements.', severity: 'error' });
      return;
    }

    // Validate client signature is not empty
    if (clientSigRef.current?.isEmpty()) {
      setAlert({ open: true, message: 'Please provide your signature.', severity: 'error' });
      return;
    }

    // Validate piercer signature is not empty
    if (piercerSigRef.current?.isEmpty()) {
      setAlert({ open: true, message: 'Piercer signature is required.', severity: 'error' });
      return;
    }

    // Validate piercer date
    if (!formData.piercerDate) {
      setAlert({ open: true, message: 'Piercer date is required.', severity: 'error' });
      return;
    }

    // Validate photo ID upload
    if (!photoIdFile) {
      setAlert({ open: true, message: 'Please upload a photo of your ID.', severity: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      const apiService = new PublicApiService();
      const submitData = {
        ...formData,
        clientSignature: clientSigRef.current?.toDataURL() || '',
        piercerSignature: piercerSigRef.current?.toDataURL() || '',
        photoIdImage: photoIdFile || '',
        photoIdVerified: 'yes',
      };
      await apiService.postConsentData(submitData);

      setFormData(initialFormState);
      clientSigRef.current?.clear();
      piercerSigRef.current?.clear();
      removePhotoId();
      setSuccessModalOpen(true);
    } catch (error) {
      setAlert({ open: true, message: 'Error submitting the form. Please try again.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const acknowledgmentStatements = [
    'This is to certify that I, the undersigned, have provided true and accurate information on this form.',
    'I fully understand that I must be 18 years of age or older, or I must have a signed and notarized parental/legal guardian permission form to obtain a body piercing.',
    'I further understand that if I give false information or provide false documents stating my name and age, I may be subject to prosecution.',
    'I certify that I am not under the influence of alcohol or drugs.',
    'I accept full responsibility for any consequences that may result from my decision to receive a body piercing.',
    'I agree not to bring legal action against Jasel, its employees, representatives, or any affiliated company for issues arising from my decision to receive a body piercing, except where prohibited by law.',
    'I acknowledge that I will receive a copy of the Body Piercing Aftercare Instructions along with verbal instructions. I agree to read and follow all aftercare instructions and accept full responsibility for the care and cleanliness of my piercing.',
    'I understand that all procedures are performed under strict hygienic conditions.',
    'I certify that I am not pregnant or breastfeeding.',
    'I certify that I have eaten within the last 4-6 hours before my appointment.',
    'I acknowledge and agree that all piercing services and session fees are non-refundable. I understand that Jasel is not responsible for lost, broken, damaged, or loosened jewelry, and I am responsible for checking my piercing jewelry regularly to ensure it remains secure and properly fastened.',
    'I agree not to bring any legal action or claim against Jasel Jewelry, its owners, employees, contractors, agents, affiliated companies, or associated personnel for issues arising from or related to my voluntary decision to receive tattoo and/or body piercing services at this establishment, except to the extent such waiver or limitation is prohibited by applicable law.',
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 5, mb: 5 }}>
      {alert.open && (
        <Stack sx={{ width: '100%', mb: 2 }} spacing={2}>
          <Alert
            severity={alert.severity}
            onClose={() => setAlert({ ...alert, open: false })}
          >
            <AlertTitle>{alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}</AlertTitle>
            {alert.message}
          </Alert>
        </Stack>
      )}

      <Card sx={{ p: 4, boxShadow: 3 }}>
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
          PIERCING CONSENT FORM
        </Typography>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Personal Information */}
          <Stack spacing={2} direction="row">
            <TextField
              fullWidth label="First Name" name="firstName"
              value={formData.firstName} onChange={handleChange} required
            />
            <TextField
              fullWidth label="Last Name" name="lastName"
              value={formData.lastName} onChange={handleChange} required
            />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Phone" type="tel" name="phone"
              value={formData.phone ? `+1 ${formData.phone}` : '+1 '}
              onChange={handlePhoneChange} required
              error={Boolean(phoneError)} helperText={phoneError}
            />
            <TextField
              fullWidth label="Email" type="email" name="email"
              value={formData.email} onChange={handleChange} required
            />
          </Stack>

          <Stack spacing={2} direction="row" sx={{ mt: 2 }}>
            <TextField
              fullWidth label="Date of Birth" type="date" name="dateOfBirth"
              value={formData.dateOfBirth} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Age" name="age" value={formData.age}
              onChange={handleChange} required sx={{ minWidth: 100 }}
            />
            <TextField
              fullWidth label="Gender" name="gender"
              value={formData.gender} onChange={handleChange}
            />
          </Stack>

          <TextField
            fullWidth label="Address" name="address"
            value={formData.address} onChange={handleChange} required
            sx={{ mt: 2 }}
          />

          <TextField
            fullWidth label="Physician" name="physician"
            value={formData.physician} onChange={handleChange}
            sx={{ mt: 2 }}
          />

          {/* Medical History */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Do you have any:
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth label="Allergies" name="allergies"
              value={formData.allergies} onChange={handleChange}
            />
            <TextField
              fullWidth label="Bleeding Disorders" name="bleedingDisorders"
              value={formData.bleedingDisorders} onChange={handleChange}
            />
            <TextField
              fullWidth label="Medical Conditions" name="medicalConditions"
              value={formData.medicalConditions} onChange={handleChange}
            />
            <TextField
              fullWidth label="Medications" name="medications"
              value={formData.medications} onChange={handleChange}
            />
          </Stack>

          {/* Emergency & Referral */}
          <Divider sx={{ my: 3 }} />
          <TextField
            fullWidth label="Emergency Contact (Name & Phone)" name="emergencyContact"
            value={formData.emergencyContact} onChange={handleChange} required
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth label="How did you hear about us?" name="howDidYouHear"
            value={formData.howDidYouHear} onChange={handleChange}
            sx={{ mt: 2 }}
          />

          {/* Consent & Acknowledgment */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Consent & Acknowledgment
          </Typography>

          <Stack spacing={1.5}>
            {acknowledgmentStatements.map((statement, index) => (
              <Card key={index} sx={{ display: 'flex', alignItems: 'flex-start', p: 2, boxShadow: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.acknowledgments[index]?.checked || false}
                      onChange={() => handleAcknowledgment(index, statement)}
                      sx={{ mr: 1 }}
                    />
                  }
                  label={<Typography variant="body2">{statement}</Typography>}
                  sx={{ alignItems: 'flex-start', m: 0 }}
                />
              </Card>
            ))}
          </Stack>

          {/* Procedure Information */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Procedure Information
          </Typography>

          <Stack spacing={2}>
            <TextField
              fullWidth label="Type of Piercing" name="typeOfPiercing"
              value={formData.typeOfPiercing} onChange={handleChange} required
            />
            <TextField
              fullWidth label="Type of Jewelry Used" name="typeOfJewelryUsed"
              value={formData.typeOfJewelryUsed} onChange={handleChange} required
            />
            <TextField
              fullWidth label="Name of Piercer" name="nameOfPiercer"
              value={formData.nameOfPiercer} onChange={handleChange} required
            />
          </Stack>

          {/* Signatures */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Signatures
          </Typography>

          {/* Client Signature */}
          <Box sx={{ mb: 3 }}>
            <SignaturePad ref={clientSigRef} label="Client Signature" />
            <TextField
              label="Date" type="date" name="date"
              value={formData.date} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
              sx={{ ml: 2, mt: 1, minWidth: 180 }}
              size="small"
            />
          </Box>

          {/* Piercer Signature */}
          <Box sx={{ mb: 3 }}>
            <SignaturePad ref={piercerSigRef} label="Piercer Signature" />
            <TextField
              label="Date" type="date" name="piercerDate"
              value={formData.piercerDate} onChange={handleChange} required
              InputLabelProps={{ shrink: true }}
              sx={{ ml: 2, mt: 1, minWidth: 180 }}
              size="small"
            />
          </Box>

          {/* Photo ID Upload */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="h5" sx={{ mb: 2, color: '#555', fontWeight: 'bold' }}>
            Photo ID
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please upload a clear photo of your government-issued ID (driver's license, passport, etc.)
          </Typography>

          {/* Hidden file inputs */}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />

          {!photoIdFile ? (
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Photo
              </Button>
              <Button
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => cameraInputRef.current?.click()}
              >
                Take Photo
              </Button>
            </Stack>
          ) : (
            <Box>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 1 }}>
                <img
                  src={photoIdFile}
                  alt="Photo ID"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '250px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                  }}
                />
                <IconButton
                  onClick={removePhotoId}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                  }}
                  size="small"
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {photoIdFileName}
              </Typography>
            </Box>
          )}

          {/* Submit */}
          <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 4 }} disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit Consent Form'}
          </Button>
        </Box>
      </Card>
      {/* Success Modal */}
      <Dialog
        open={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title">✅ Form Submitted</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your consent form has been submitted successfully! Thank you.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuccessModalOpen(false)} variant="contained" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConsentForm;
